import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Button,
    Checkbox,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { Text } from '@hubdevs/viu-ui-shared';
import { ApiServiceContext } from '../../api/ApiService.context';
import {
    AttributionSummary,
    JoinedOrganizationAttributionSummary,
    OrganizationAttributionSummary,
} from '../../api/attribution/types';
import { AppFormRow } from '../../components/AppFormRow/AppFormRow';
import { AppTableWithButtons } from '../../components/TableWithButtons/TableWithButtons';
import { AppPage } from '../../components/Page/Page';
import { AppFormCell } from '../../components/AppFormCell/AppFormCell';
import { ApiServiceEndpointResponse } from '../../api/api.types';
import { SortConfig, DEFAULT_SORT } from '../../utils/sortUtils';
import { APIQuery } from '../../utils/sharedTypes';
import { AttributionService } from '../../api/attribution/AttributionService.class';
import { OrganizationService } from '../../api/organization/OrganizationService.class';
import { ApiServiceCollection } from '../../api/ApiServiceCollection.class';


type CheckboxCheckedHandler = (checked: boolean, props: GridRenderCellParams<any, boolean>) => void;

type LoadingState = 'NOT STARTED' | 'LOADING' | 'SUCCESS' | 'FAIL';
type OrgLoadingState = 'WAITING' | 'LOADING' | 'SUCCESS' | 'FAIL';

type AttributionLoadingState = 'LOADING' | 'NOT LOADING';

type AttributionState = JoinedOrganizationAttributionSummary & {
    loadingState: AttributionLoadingState;
}

type AttributionAssociationCommand = {
    attributionGuid: string;
    included: boolean;
    required: boolean;
    action: 'CREATE' | 'UPDATE' | 'DELETE';
}

// We need to first load all of the OrganizationAttributions for the
// current org. This page size will be used to paginate through all
// initial OrganizationAttributions for this organization (must eb less than 1000).
const ORG_ATTR_PAGE_SIZE = 100;

export const AssociateAttributionsPage = () => {
    const navigate = useNavigate();
    const { organizationGuid } = useParams();
    const apiServiceContext = useContext(ApiServiceContext);
    const {
        AttributionService,
        OrganizationService
    } = apiServiceContext.getApiServiceCollections() as {
        AttributionService: AttributionService,
        OrganizationService: OrganizationService,
        [key: string]: ApiServiceCollection
    };
    const [attributions, setAttributions] = useState<AttributionSummary[]>([]);
    const [attrTotal, setAttrTotal] = useState<number>(0);
    const [attrPage, setAttrPage] = useState<number>(0);
    const [attrPageSize, setAttrPageSize] = useState<number>(10);
    const [attrLoadingState, setAttrLoadingState] = useState<LoadingState>('NOT STARTED');
    const [attrError, setAttrError] = useState<string>('');
    const [attrSort, setAttrSort] = useState<SortConfig | null>(DEFAULT_SORT);
    const [attrDisplayNameFilter, setAttrDisplayNameFilter] = useState<string>('');
    const [hasLoadedAttributions, setHasLoadedAttributions] = useState<boolean>(false);

    const [orgAttributions, setOrgAttributions] = useState<OrganizationAttributionSummary[]>([]);
    const [orgAttrTotal, setOrgAttrTotal] = useState<number>(1000);
    const [orgAttrPage, setOrgAttrPage] = useState<number>(0);
    const [orgAttrLastPage, setOrgAttrLastPage] = useState<number>(-1);
    const [orgAttributionsError, setOrgAttributionsError] = useState<string>('');
    const [orgAttrLoadingState, setOrgAttrLoadingState] = useState<OrgLoadingState>('WAITING');

    const [attributionDataProcessed, setAttributionDataProcessed] = useState<boolean>(false);
    const [joinedAttributions, setJoinedAttributions] = useState<JoinedOrganizationAttributionSummary[]>([]);
    const [attributionState, setAttributionState] = useState<Record<string, AttributionState>>({});
    const [commands, setCommands] = useState<AttributionAssociationCommand[]>([]);

    const [commandProcessingState, setCommandProcessingState] = useState<AttributionLoadingState>('NOT LOADING');

    const organizationAttributionsMap = useMemo(() => {
        if (orgAttrLoadingState !== 'SUCCESS') {
            return {};
        }
        return orgAttributions.reduce((acc, cur) => {
            acc[cur.attributionGuid] = cur;
            return acc;
        }, {} as Record<string, OrganizationAttributionSummary>)
    }, [orgAttributions, orgAttrLoadingState]);

    const requiredAttributions = useMemo(() => {
        return Object.values(organizationAttributionsMap)
            .filter(attr => attr.required)
    }, [organizationAttributionsMap]);

    const isTypeAlreadyRequired = useMemo(() => {
        return requiredAttributions.reduce((acc, cur) => {
            acc[cur.type] = true;
            return acc;
        }, {} as Record<string, boolean>);
    }, [requiredAttributions]);

    useEffect(() => {
        if (attributionDataProcessed) {
            return;
        }
        setAttributionDataProcessed(true);
        const localJoinedAttr = attributions.map(attr => {
            const orgAttr = organizationAttributionsMap[attr.attributionGuid];
            return {
                ...attr,
                organizationGuid: organizationGuid!,
                organizationAttributionGuid: orgAttr?.organizationAttributionGuid,
                required: !!orgAttr?.required,
                included: !!orgAttr
            }
        });
        setAttributionState({
            ...attributionState,
            ...localJoinedAttr.reduce((acc, cur) => {
                acc[cur.attributionGuid] = {
                    ...cur,
                    loadingState: 'NOT LOADING'
                }
                return acc;
            }, {} as Record<string, AttributionState>)
        });
        setJoinedAttributions(localJoinedAttr);
    }, [attributions, organizationAttributionsMap, organizationGuid, attributionState, attributionDataProcessed]);

    const loadOrganizationAttributionsPage = useCallback(async () => {
        setOrgAttributionsError('');
        // NOTE: this should not be true - if we're on this page we should have an organizationGuid
        if (!organizationGuid) {
            setOrgAttrLoadingState('SUCCESS');
            // setOrgAttributions([]);
            return;
        }
        if (orgAttrPage * ORG_ATTR_PAGE_SIZE >= orgAttrTotal) {
            setOrgAttrLoadingState('SUCCESS');
            return;
        }
        console.log('Loading page of Organization Attributions');
        // Data not immediately displayed in table. No need to track retries.
        let orgAttrsResp = await OrganizationService.getOrganizationAttributions({
            organizationGuid,
            skip: orgAttrPage * ORG_ATTR_PAGE_SIZE,
            limit: ORG_ATTR_PAGE_SIZE
        });
        while (orgAttrsResp.retry) {
            orgAttrsResp = await orgAttrsResp.retry.catch(error => error);
        }
        if (orgAttrsResp.error) {
            setOrgAttributionsError('Failed to load organization attributions');
            setOrgAttrLoadingState('FAIL');
            return
        }
        setOrgAttributions([
            ...orgAttributions,
            ...orgAttrsResp.jsonBody!.data
        ]);
        setOrgAttrTotal(orgAttrsResp.jsonBody!.total);
        setOrgAttrPage(orgAttrPage + 1);
        setOrgAttrLoadingState('WAITING');
    }, [organizationGuid, orgAttrPage, orgAttrTotal, OrganizationService, orgAttributions]);

    const loadAllAttributionsPage = useCallback(async ({ page, pageSize, query }: { page: number, pageSize: number, query?: APIQuery }) => {
        setAttrError('');
        setAttrLoadingState('LOADING');
        let attrResp = await AttributionService.getAttributions({
            skip: page * pageSize,
            limit: pageSize,
            query
        });
        while (attrResp.retry) {
            attrResp = await attrResp.retry.catch(error => error);
        }
        if (attrResp.error) {
            setAttrError('Failed to load all attributions');
            setAttrLoadingState('FAIL');
            return
        }
        setAttrTotal(attrResp.jsonBody!.total);
        setAttributions(attrResp.jsonBody!.data);
        setAttrLoadingState('SUCCESS');
        setHasLoadedAttributions(true);
        setAttributionDataProcessed(false);
    }, [AttributionService]);

    // ****************
    // * PAGE LOADING *
    // ****************


    // Calling this method should trigger the following useEffect
    // which will load all OrganizationAttributions
    const reloadOrgAttributions = useCallback(() => {
        console.log('Reloading Organization Attributions');
        setOrgAttrLoadingState('WAITING');
        setOrgAttrPage(0);
        setOrgAttrLastPage(-1);
    }, []);

    useEffect(() => {
        // Load all pages of OrganizationAttributions
        if (orgAttrLoadingState === 'WAITING' && orgAttrPage > orgAttrLastPage) {
            setOrgAttrLastPage(orgAttrPage);
            setOrgAttrLoadingState('LOADING');
            loadOrganizationAttributionsPage();
        }
    }, [orgAttrLoadingState, orgAttrPage, orgAttrLastPage, loadOrganizationAttributionsPage]);

    useEffect(() => {
        if (orgAttrLoadingState === 'SUCCESS' && attrLoadingState === 'NOT STARTED') {
            // Load first page of Attributions
            loadAllAttributionsPage({
                page: attrPage,
                pageSize: attrPageSize
            });
        }
    }, [orgAttrLoadingState, attrLoadingState, loadAllAttributionsPage, attrPage, attrPageSize]);


    const processCommand = useCallback((command: AttributionAssociationCommand) => {
        new Promise<ApiServiceEndpointResponse<any>>((resolve) => {
            switch (command.action) {
                case 'CREATE':
                    const createOrganizationAttribution = OrganizationService.createOrganizationAttribution;
                    resolve(createOrganizationAttribution({
                        organizationGuid,
                        attributionGuid: command.attributionGuid,
                        required: command.required
                    }));
                    break;
                case 'UPDATE':
                    const updateOrganizationAttribution = OrganizationService.updateOrganizationAttribution;
                    resolve(updateOrganizationAttribution({
                        organizationGuid,
                        attributionGuid: command.attributionGuid,
                        required: command.required
                    }));
                    break;
                case 'DELETE':
                    const removeOrganizationAttribution = OrganizationService.removeAttribution;
                    resolve(removeOrganizationAttribution({
                        organizationGuid,
                        attributionGuid: command.attributionGuid
                    }));
                    break;
            }
        })
            .then(data => {
                console.log('Associate Attributions Command Completed');
                const stateUpdate: Partial<AttributionState> = {
                    loadingState: 'NOT LOADING'
                };
                if (command.action === 'CREATE') {
                    stateUpdate.organizationAttributionGuid = data.textBody!;
                }
                setAttributionState(prev => ({
                    ...prev,
                    [command.attributionGuid]: {
                        ...prev[command.attributionGuid],
                        ...stateUpdate
                    }
                }));
                setCommandProcessingState('NOT LOADING');
                reloadOrgAttributions();
            })
    }, [OrganizationService, organizationGuid, reloadOrgAttributions]);


    const includedCheckboxCallback: CheckboxCheckedHandler = useCallback((checked, props) => {
        const rowData = props?.row as JoinedOrganizationAttributionSummary | undefined;
        if (!rowData) {
            return
        }
        const attrsAssoc = attributionState[rowData.attributionGuid];
        const required = attrsAssoc.required || false;

        setCommands([
            ...commands,
            {
                attributionGuid: attrsAssoc.attributionGuid,
                required: checked ? required : false,
                action: checked ? 'CREATE' : 'DELETE',
                included: checked
            }
        ]);
        setAttributionState({
            ...attributionState,
            [attrsAssoc.attributionGuid]: {
                ...attrsAssoc,
                required: checked ? required : false,
                included: checked,
                loadingState: 'LOADING'
            }
        });
    }, [commands, attributionState]);

    const requiredCheckboxCallback: CheckboxCheckedHandler = useCallback((checked, props) => {
        const rowData = props?.row as JoinedOrganizationAttributionSummary | undefined;
        if (!rowData) {
            return
        }
        const attrsAssoc = attributionState[rowData.attributionGuid];
        const included = attrsAssoc.included || false;

        setCommands([
            ...commands,
            {
                attributionGuid: attrsAssoc.attributionGuid,
                required: checked,
                action: included ? 'UPDATE' : 'CREATE',
                included: true
            }
        ]);
        const updatedState: AttributionState = {
            ...attrsAssoc,
            required: checked,
            included: true,
            loadingState: 'LOADING' as AttributionLoadingState
        };

        setAttributionState(prev => ({
            ...prev,
            [attrsAssoc.attributionGuid]: updatedState
        }));

    }, [commands, attributionState]);

    const renderCheckBox = useCallback((handler: CheckboxCheckedHandler, checkedKey: string) => (props: GridRenderCellParams<any, boolean>) => {
        const rowData = props?.row as JoinedOrganizationAttributionSummary | undefined;
        if (!rowData) return;

        const attribution = attributionState[rowData.attributionGuid];

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            if (handler) handler(event.target.checked, props);
        };


        return (
            <Checkbox
                checked={checkedKey === 'required' ? attribution.required : attribution.included}
                onChange={handleChange}
                disabled={
                    checkedKey === 'required' &&
                    !attribution.required &&
                    isTypeAlreadyRequired[attribution.type]
                }
                data-testid={`check-attr-${attribution.attributionGuid}-${checkedKey}`}
            />
        );
    }, [attributionState, isTypeAlreadyRequired]);



    const columns = useMemo(() => {
        return [
            {
                field: 'included',
                headerName: 'Included',
                width: 100,
                editable: true,
                type: 'boolean',
                valueGetter: (value: any, row: any) => {
                    const attr = attributionState[row.attributionGuid];
                    return !!attr?.included;
                },
                renderCell: renderCheckBox(includedCheckboxCallback, 'included'),
            },
            {
                field: 'required',
                headerName: 'Required',
                width: 100,
                editable: true,
                type: 'boolean',
                valueGetter: (value: any, row: any) => {
                    const attr = attributionState[row.attributionGuid];
                    return !!attr?.required;
                },
                renderCell: renderCheckBox(requiredCheckboxCallback, 'required'),
            },
            {
                field: 'displayName',
                headerName: 'Name',
                width: 300,
                editable: false,
            },
            {
                field: 'type',
                headerName: 'Type',
                width: 150,
                editable: false,
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 100,
                editable: false,
            },
        ] as GridColDef<JoinedOrganizationAttributionSummary>[];
    }, [attributionState, includedCheckboxCallback, requiredCheckboxCallback, renderCheckBox]);

    // PROCESS STATE UPDATE COMMANDS
    useEffect(() => {
        if (!commands.length || commandProcessingState === 'LOADING') {
            return
        }
        const commandsCopy = [...commands];
        const nextCommand = commandsCopy.shift();
        setCommands(commandsCopy);
        setCommandProcessingState('LOADING');
        setAttributionState({
            ...attributionState,
            [nextCommand!.attributionGuid]: {
                ...attributionState[nextCommand!.attributionGuid],
                loadingState: 'LOADING'
            }
        });
        processCommand(nextCommand!);
    }, [commands, commandProcessingState, attributionState, processCommand]);

    const handlePageChange = useCallback((page: number, pageSize: number) => {
        setAttrPage(page);
        setAttrPageSize(pageSize);
        loadAllAttributionsPage({
            page,
            pageSize,
            query: {
                filter: {
                    displayName: attrDisplayNameFilter
                },
                sort: attrSort || undefined
            }
        });
    }, [setAttrPage, setAttrPageSize, loadAllAttributionsPage, attrDisplayNameFilter, attrSort]);

    const handleAttributionSearch = useCallback((searchTerm: string) => {
        setAttrDisplayNameFilter(searchTerm);
        setAttrPage(0);
        loadAllAttributionsPage({ page: 0, pageSize: attrPageSize, query: {
                filter: {
                    displayName: searchTerm
                },
                sort: attrSort || undefined
            } 
        });
    }, [loadAllAttributionsPage, attrPageSize, attrSort]);

    const handleSortChange = useCallback((sort: SortConfig | null) => {
        const effectiveSort = sort || DEFAULT_SORT;
        setAttrSort(effectiveSort);
        setAttrPage(0);
        loadAllAttributionsPage({ page: 0, pageSize: attrPageSize, query: {
                filter: {
                    displayName: attrDisplayNameFilter
                },
                sort: effectiveSort || undefined
            } 
        });
    }, [loadAllAttributionsPage, attrPageSize, attrDisplayNameFilter]);

    const handleUnsetRequired = (attributionGuid: string) => {
        const target = attributionState[attributionGuid];
        setCommands([
            ...commands,
            {
                attributionGuid,
                required: false,
                included: true,
                action: 'UPDATE'
            }
        ]);
        setAttributionState(prev => ({
            ...prev,
            [attributionGuid]: {
                ...target,
                required: false,
                loadingState: 'LOADING'
            }
        }));
        setOrgAttrLoadingState('LOADING');
    };

    const isPageLoading = useMemo(() => (
        attrLoadingState === 'LOADING' ||
        orgAttrLoadingState === 'WAITING' ||
        orgAttrLoadingState === 'LOADING' ||
        commandProcessingState === 'LOADING'
    ), [attrLoadingState, orgAttrLoadingState, commandProcessingState]);



    function attributionForm() {
        return (
            <AppFormRow>
                <AppTableWithButtons
                    rows={joinedAttributions}
                    rowIdField='attributionGuid'
                    columns={columns}
                    title='Attributions associated with this Organization'
                    pagination
                    totalRecords={attrTotal}
                    isLoading={attrLoadingState === 'LOADING' || orgAttrLoadingState === 'WAITING' || orgAttrLoadingState === 'LOADING'}
                    onPageChange={handlePageChange}
                    page={attrPage}
                    defaultSort={DEFAULT_SORT}
                    onSortChange={handleSortChange}
                    showSearch
                    onSearch={handleAttributionSearch}
                />
            </AppFormRow>
        )
    }

    function renderRequiredAttributionsTable() {
        return (
            <AppFormRow>
                <AppTableWithButtons
                    rows={requiredAttributions}
                    rowIdField='attributionGuid'
                    columns={[
                        { field: 'displayName', headerName: 'Name', width: 300 },
                        { field: 'type', headerName: 'Type', width: 150 },
                        {
                            field: 'actions',
                            headerName: 'Actions',
                            width: 150,
                            renderCell: (params) => (
                                <Button
                                    variant="outlined"
                                    onClick={() => handleUnsetRequired(params.row.attributionGuid)}
                                >
                                    Unset
                                </Button>
                            )
                        }
                    ]}
                    title='Required Attributions'
                />
            </AppFormRow>
        );
    }



    function getFormDisplay() {
        const errors = (
            <>
                {attrError ? <Text>{attrError}</Text> : null}
                {orgAttributionsError ? <Text>{orgAttributionsError}</Text> : null}
            </>
        );
        if (hasLoadedAttributions) {
            return (
                <>
                    {errors}
                    {attributionForm()}
                </>
            );
        }
        if (attrLoadingState === 'FAIL' || orgAttrLoadingState === 'FAIL') {
            return errors;
        }
        return null;
    }

    // goal: make this generic so it can be used to associate attributions to either:
    // 1) organizations
    // 2) bundles
    return (
        <AppPage
            title="Associate Attributions"
            isLoading={isPageLoading}
            buttonRowContent={(
                <AppFormCell>
                    <Button variant="outlined" onClick={() => navigate(`/organization/${organizationGuid}`)} >Close</Button>
                </AppFormCell>
            )}
        >
            {renderRequiredAttributionsTable()}
            {getFormDisplay()}
        </AppPage>
    )
}